import {AfterViewInit, Component, DoCheck, ElementRef, OnDestroy, OnInit, ViewChild} from '@angular/core';
import {PlanModelService, Surveyor} from '../../common/service/plan-model.service';
import {PDF_CREATION_METHOD} from '../../common/constants';
import {NgClass} from '@angular/common';
import {ControlContainer, FormsModule, NgForm} from '@angular/forms';
import {LookupService} from '../../common/service/lookup.service';
import {NgEventBus} from 'ng-event-bus';
import {PermissionsService} from '../../common/service/permissions.service';
import {CertificationType} from '../../common/models/certification-type.model';
import {debounceTime, distinctUntilChanged, map, Observable, Subscription, tap} from 'rxjs';
import {NlrTypeheadDirective} from '../../common/directive/nlr-typehead.directive';
import {NlrFormFieldHasErrorPipe} from '../../common/pipe/nlr-form-field-has-error.pipe';

@Component({
    selector: 'certification-survey-regulatory-statement',
    imports: [NgClass, FormsModule, NlrTypeheadDirective, NlrFormFieldHasErrorPipe],
    templateUrl: './certification-survey-regulatory-statement.component.html',
    styleUrl: './certification-survey-regulatory-statement.component.scss',
    viewProviders: [{provide: ControlContainer, useExisting: NgForm}]
})
export class CertificationSurveyRegulatoryStatementComponent implements OnInit, OnDestroy, AfterViewInit, DoCheck {
    // Digital statutory plans (FH_STAT/CR_STAT lodged via Digital Plan) are still a Regulation 54 certification, but present
    // generic wording rather than the "Reg. 54" lookup labels. These overrides are applied for display only - the shared
    // certificationType lookup object is never mutated, so switching plan types leaves nothing to clean up.
    private static readonly STATUTORY_INSTRUCTION_LABEL = 'Verify and update the certification text';

    private static readonly DEFAULT_TYPE_DISPLAY_LABEL = 'Certification text';

    private static readonly STATUTORY_CERTIFICATION_TEXT = 'For statutory purposes only.';

    title = 'Certification and survey regulatory statement';

    surveyStatementTitle = 'Survey regulatory statement';

    permissions = ['EDIT_CERTIFICATION', 'VIEW_CERTIFICATION'];

    certificationTypes: CertificationType[];

    surveyors: Surveyor[];

    certifyingSurveyorName = '';

    getSurveyorsSubscription: Subscription;

    private pdfCreationMethodSubscription: Subscription;

    @ViewChild('certifyingSurveyorInput')
    certifyingSurveyorInput: ElementRef;

    constructor(
        private eventBus: NgEventBus,
        private permissionsService: PermissionsService,
        private lookupService: LookupService,
        private planModelService: PlanModelService
    ) {}

    ngOnInit(): void {
        this.certificationTypes = this.lookupService.getLookups('certificationType');

        this.getSurveyorsSubscription = this.lookupService.retrieveLookup<Surveyor[]>('/surveyors/eligible').subscribe({
            next: data => {
                this.surveyors = data;
                console.log('Eligible surveyors loaded');
            },
            error: error => {
                console.error('Eligible surveyors load failed: ' + error.message);
            }
        });

        this.eventBus.cast('nlr:busy', this.getSurveyorsSubscription);

        // The plan's certifying surveyor is cleared when its PDF creation method changes, so drop the name held here for
        // display. Held separately from the plan for the type-ahead search, and only ever populated from the plan below.
        this.pdfCreationMethodSubscription = this.eventBus
            .on('nlr:plan-model-updated:pdfCreationMethod')
            .subscribe(() => (this.certifyingSurveyorName = ''));
    }

    ngOnDestroy() {
        this.getSurveyorsSubscription.unsubscribe();
        this.pdfCreationMethodSubscription.unsubscribe();
    }

    ngAfterViewInit(): void {
        this.certifyingSurveyorInput.nativeElement.focus();
    }

    ngDoCheck(): void {
        const certifyingSurveyor = this.getPlanModel().certifyingSurveyor;
        if (certifyingSurveyor) {
            this.certifyingSurveyorName = this.getSurveyorName(certifyingSurveyor);
        }
    }

    showComponent(): boolean {
        return this.permissionsService.hasPermission(this.permissions);
    }

    isReadonly(): boolean {
        return this.planModelService.readonly || !this.permissionsService.hasEditPermission(this.permissions);
    }

    getPlanModel() {
        return this.planModelService.getModel();
    }

    getCertification() {
        return this.getPlanModel().certification;
    }

    getCertificationType() {
        return this.getPlanModel().certificationType;
    }

    getSurveyMethod() {
        return this.getPlanModel().surveyMethod;
    }

    isDigitalOrPortalPlan() {
        const pdfCreationMethod = this.getPlanModel().pdfCreationMethod;
        return pdfCreationMethod == PDF_CREATION_METHOD.DIGITAL_PLAN || pdfCreationMethod == PDF_CREATION_METHOD.SURVEYOR_PORTAL;
    }

    isStatutoryPlan() {
        const id = this.getPlanModel().planPurpose?.id;
        return id === 'FH_STAT' || id === 'CR_STAT';
    }

    isDigitalStatutoryPlan() {
        return this.getPlanModel().pdfCreationMethod == PDF_CREATION_METHOD.DIGITAL_PLAN && this.isStatutoryPlan();
    }

    // Heading above the certification text. For digital statutory plans it is overridden to generic wording; otherwise it is
    // the certificationInstructionLabel from the (Regulation 54) lookup.
    getCertificationInstructionLabel() {
        return this.isDigitalStatutoryPlan()
            ? CertificationSurveyRegulatoryStatementComponent.STATUTORY_INSTRUCTION_LABEL
            : this.getCertificationType()?.certificationInstructionLabel;
    }

    // Label for the certification text field. Digital statutory plans have an empty certificationTypeLabel, so fall back to a
    // generic default; other plans keep the lookup's certificationTypeLabel (e.g. "Regulation 54").
    getCertificationTypeDisplayLabel() {
        if (this.isDigitalStatutoryPlan()) {
            return CertificationSurveyRegulatoryStatementComponent.DEFAULT_TYPE_DISPLAY_LABEL;
        }
        return this.getCertificationType()?.certificationTypeLabel || CertificationSurveyRegulatoryStatementComponent.DEFAULT_TYPE_DISPLAY_LABEL;
    }

    updateCertificationType() {
        // Digital statutory plans have no survey method to reset from, so Reset restores the statutory default text.
        if (this.isDigitalStatutoryPlan()) {
            this.getCertification().certificationText = CertificationSurveyRegulatoryStatementComponent.STATUTORY_CERTIFICATION_TEXT;
            return;
        }

        const certificationLookupId = this.getPlanModel().surveyMethod?.certificationlookupId;
        if (certificationLookupId) {
            this.getPlanModel().certificationType = this.certificationTypes[certificationLookupId - 1];
            if (this.getPlanModel().certificationType) {
                this.getCertification().certificationType = Object.assign({}, this.getCertificationType());
                this.getCertification().certificationText = this.getCertificationType().certificationText.replace(/<br>/g, '\n');
            }
        }
    }

    updateRegulatoryStatement() {
        if (this.getSurveyMethod()?.regulatoryStatementApplies) {
            this.getCertification().surveyRegulatoryStatementApplies = true;
            if (this.getSurveyMethod().regulatoryStatement) {
                this.getCertification().surveyRegulatoryStatement = this.getSurveyMethod().regulatoryStatement.replace(/<br>/g, '\n');
            }
        }
    }

    surveyorNameSearch: (text$: Observable<string>) => Observable<string[]> = (text$: Observable<string>) =>
        text$.pipe(
            debounceTime(200),
            distinctUntilChanged(),
            tap(() => {
                if (this.getPlanModel().certifyingSurveyor) {
                    delete this.getPlanModel().certifyingSurveyor;
                }
            }),
            map(term => (term.length < 1 ? [] : this.getFilteredSurveyorNames(term)))
        );

    surveyorSelected(surveyorName: string) {
        this.getPlanModel().certifyingSurveyor = this.surveyors.find(surveyor => this.getSurveyorName(surveyor) === surveyorName);
    }

    private getFilteredSurveyorNames(viewValue: string) {
        return this.surveyors
            .filter(surveyor => `${surveyor.familyName} ${surveyor.givenNames}`.toLowerCase().startsWith(viewValue.toLowerCase()))
            .map(surveyor => this.getSurveyorName(surveyor));
    }

    private getSurveyorName(surveyor: Surveyor) {
        return `${surveyor.familyName} ${surveyor.givenNames}`;
    }
}
